<template>
  <div class="w-full py-2">
    <a href="/billing/dashboard" class="text-sm font-semibold leading-6 text-gray-900">Dashboard <span aria-hidden="true">→</span></a>
  </div>
  <div class="relative isolate px-6 pt-14">
    <!-- Header with billing year info -->
    <div class="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-bold text-indigo-900">Year Level Advancement for {{ billingYear }}</h2>
          <p class="text-sm text-indigo-700">This will update all students to their expected year level for the {{ billingYear }} billing year.</p>
        </div>
        <div class="text-right">
          <div class="text-2xl font-bold text-indigo-600">{{ billingYear }}</div>
          <div class="text-xs text-indigo-500">Billing Year</div>
        </div>
      </div>
    </div>

    <div class="mb-4 flex justify-between items-center">
      <div class="flex gap-2">
        <button @click="updateAllYearLevels" class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded">
          Advance All Students to {{ billingYear }}
        </button>
        <button @click="exportToCsv" class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
          Export CSV
        </button>
        <label class="bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-4 rounded cursor-pointer">
          Import CSV
          <input type="file" accept=".csv" @change="importFromCsv" class="hidden" />
        </label>
      </div>
      <div class="flex items-center">
        <label class="inline-flex items-center mr-4">
          <input
            type="checkbox"
            v-model="hideAlumni"
            class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          >
          <span class="ml-2 text-sm text-gray-700">Hide Alumni Students</span>
        </label>
        <div v-if="message" :class="['px-4 py-2 rounded', message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700']">
          {{ message.text }}
        </div>
      </div>
    </div>
    <div class="mb-4">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search by name or ID..."
        class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
      />
    </div>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-300">
        <thead>
          <tr>
            <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Student Name</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Start Level</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Start Year</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Current</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Expected ({{ billingYear }})</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
            <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-for="student in filteredStudents"
              :key="student._id"
              :class="{
                'bg-purple-50': !student.firstSystemYearLevel || !student.yearLevelStartDate,
                'bg-amber-50': student.firstSystemYearLevel && student.yearLevelStartDate && student.currentYear !== calculateExpectedYearLevel(student, billingYear) && student.academicYear !== billingYear,
                'bg-blue-50': student.firstSystemYearLevel && student.yearLevelStartDate && student.currentYear === calculateExpectedYearLevel(student, billingYear) && student.academicYear !== billingYear,
                'bg-green-50': student.academicYear === billingYear && student.firstSystemYearLevel && student.yearLevelStartDate
              }">
            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
              {{ student.firstNameLegal }} {{ student.lastNameLegal }} [{{ student.heroId }}]
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ student.firstSystemYearLevel || '—' }}</td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ student.yearLevelStartDate || '—' }}</td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-medium">{{ student.currentYear }}</td>
            <td class="whitespace-nowrap px-3 py-4 text-sm">
              <span :class="{
                'text-green-600 font-medium': student.currentYear === calculateExpectedYearLevel(student, billingYear),
                'text-amber-600 font-bold': student.currentYear !== calculateExpectedYearLevel(student, billingYear)
              }">
                {{ calculateExpectedYearLevel(student, billingYear) }}
              </span>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm">
              <span v-if="student.academicYear === billingYear && student.firstSystemYearLevel && student.yearLevelStartDate" class="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                Up to date
              </span>
              <span v-else-if="!student.firstSystemYearLevel || !student.yearLevelStartDate" class="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                Needs baseline
              </span>
              <span v-else-if="student.currentYear === calculateExpectedYearLevel(student, billingYear)" class="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                Year OK, sync needed
              </span>
              <span v-else class="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                Needs advancement ({{ student.academicYear || 'No year' }})
              </span>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              <div class="flex gap-2">
                <select
                  v-model="student.currentYear"
                  class="block rounded-md border-0 py-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm"
                >
                  <option v-for="year in yearLevels" :key="year" :value="year">{{ year }}</option>
                </select>
                <button
                  @click="saveStudent(student)"
                  class="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-xs"
                >
                  Save
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

const students = ref([]);
const message = ref(null);
const searchQuery = ref('');
const hideAlumni = ref(true); // Default to hiding Alumni students
const billingYear = ref(new Date().getFullYear()); // Will be loaded from settings
const yearLevels = [
  'JPRE',
  'PRE',
  'FND',
  'Year 1',
  'Year 2',
  'Year 3',
  'Year 4',
  'Year 5',
  'Year 6',
  'Year 7',
  'Year 8',
  'Year 9',
  'Year 10',
  'Year 11',
  'Year 12',
  'Alumni'
];

// Calculate expected year level based on firstSystemYearLevel and years since start
const calculateExpectedYearLevel = (student, targetYear) => {
  if (!student.firstSystemYearLevel || !student.yearLevelStartDate) {
    return student.currentYear; // Can't calculate, return current
  }

  const startIndex = yearLevels.indexOf(student.firstSystemYearLevel);
  if (startIndex === -1) return student.currentYear;

  const yearsAdvanced = targetYear - student.yearLevelStartDate;
  const expectedIndex = startIndex + yearsAdvanced;

  // Cap at Alumni (last level)
  if (expectedIndex >= yearLevels.length) {
    return 'Alumni';
  }
  if (expectedIndex < 0) {
    return student.firstSystemYearLevel;
  }

  return yearLevels[expectedIndex];
};

const filteredStudents = computed(() => {
  let filtered = students.value;
  
  // First filter out Alumni if hideAlumni is true
  if (hideAlumni.value) {
    filtered = filtered.filter(student => student.currentYear !== 'Alumni');
  }
  
  // Then apply search filter if there's a search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(student => {
      const fullName = `${student.firstNameLegal} ${student.lastNameLegal}`.toLowerCase();
      const heroIdStr = student.heroId?.toString() || '';
      
      return fullName.includes(query) || 
             student.firstNameLegal.toLowerCase().includes(query) || 
             student.lastNameLegal.toLowerCase().includes(query) || 
             heroIdStr.includes(query);
    });
  }
  
  return filtered;
});

const showMessage = (text, type = 'success') => {
  message.value = { text, type };
  setTimeout(() => {
    message.value = null;
  }, 3000);
};

const saveStudent = async (student) => {
  try {
    const payload = {
      _id: student._id.$oid || student._id,
      currentYear: student.currentYear,
      academicYear: billingYear.value
    };

    // Set baseline data if missing (for future year level calculations)
    if (!student.firstSystemYearLevel || !student.yearLevelStartDate) {
      payload.firstSystemYearLevel = student.currentYear;
      payload.yearLevelStartDate = billingYear.value;
    }

    console.log('Attempting to update student with payload:', payload);

    await axios.post('/api/updateStudent', payload);

    // Update the local state after successful save
    student.academicYear = billingYear.value;
    if (payload.firstSystemYearLevel) {
      student.firstSystemYearLevel = payload.firstSystemYearLevel;
      student.yearLevelStartDate = payload.yearLevelStartDate;
    }
    showMessage('Student updated successfully');
  } catch (error) {
    console.error('Error updating student. Details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      student: student._id,
      message: error.message
    });
    showMessage(error.response?.data?.error || `Failed to update student: ${error.message}`, 'error');
  }
};

const updateAllYearLevels = async () => {
  let updateCount = 0;
  let skippedCount = 0;
  let errorCount = 0;

  try {
    for (const student of students.value) {
      // Skip students already updated for this billing year
      if (student.academicYear === billingYear.value) {
        skippedCount++;
        continue;
      }

      // Calculate expected year level based on progression
      const expectedYearLevel = calculateExpectedYearLevel(student, billingYear.value);

      // Check if baseline data is missing
      const needsBaseline = !student.firstSystemYearLevel || !student.yearLevelStartDate;

      // Only update if year level needs to change, academicYear needs updating, or baseline is missing
      if (student.currentYear !== expectedYearLevel || student.academicYear !== billingYear.value || needsBaseline) {
        try {
          const payload = {
            _id: student._id.$oid || student._id,
            currentYear: expectedYearLevel,
            academicYear: billingYear.value
          };

          // Set baseline data if missing (for future year level calculations)
          if (needsBaseline) {
            payload.firstSystemYearLevel = expectedYearLevel;
            payload.yearLevelStartDate = billingYear.value;
          }

          await axios.post('/api/updateStudent', payload);

          // Update local state
          student.currentYear = expectedYearLevel;
          student.academicYear = billingYear.value;
          if (needsBaseline) {
            student.firstSystemYearLevel = expectedYearLevel;
            student.yearLevelStartDate = billingYear.value;
          }
          updateCount++;
        } catch (err) {
          console.error(`Failed to update student ${student.heroId}:`, err);
          errorCount++;
        }
      }
    }

    if (errorCount > 0) {
      showMessage(`Updated ${updateCount} students, ${skippedCount} skipped, ${errorCount} errors`, 'error');
    } else {
      showMessage(`Successfully updated ${updateCount} students for ${billingYear.value} (${skippedCount} already up to date)`);
    }

    // Refresh the list
    await fetchStudents();
  } catch (error) {
    showMessage('Error updating students', 'error');
  }
};

// Map external year level names to internal names
const yearLevelMapping = {
  'preschool': 'PRE',
  'pre': 'PRE',
  'foundation': 'FND',
  'fnd': 'FND',
  'junior preschool': 'JPRE',
  'jpre': 'JPRE',
  'year 1': 'Year 1',
  'year 2': 'Year 2',
  'year 3': 'Year 3',
  'year 4': 'Year 4',
  'year 5': 'Year 5',
  'year 6': 'Year 6',
  'year 7': 'Year 7',
  'year 8': 'Year 8',
  'year 9': 'Year 9',
  'year 10': 'Year 10',
  'year 11': 'Year 11',
  'year 12': 'Year 12',
  'year 13': 'Alumni',
  'alumni': 'Alumni'
};

const normalizeYearLevel = (level) => {
  if (!level) return null;
  const normalized = level.toString().trim().toLowerCase();
  return yearLevelMapping[normalized] || level.trim();
};

const importFromCsv = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async (e) => {
    const text = e.target.result;
    const lines = text.split(/\r?\n/).filter(line => line.trim());

    // Skip header row
    const dataLines = lines.slice(1);

    let updatedCount = 0;
    let notFoundCount = 0;
    let errorCount = 0;
    const notFoundIds = [];

    for (const line of dataLines) {
      // Parse CSV - handle tab or comma separated
      const parts = line.includes('\t') ? line.split('\t') : line.split(',');
      if (parts.length < 4) continue;

      const heroId = parseInt(parts[3]?.trim(), 10);
      const yearLevelRaw = parts[2]?.trim();
      const yearLevel = normalizeYearLevel(yearLevelRaw);

      if (!heroId || !yearLevel) continue;

      // Find the student by Hero ID
      const student = students.value.find(s => s.heroId === heroId);

      if (!student) {
        notFoundCount++;
        notFoundIds.push(heroId);
        continue;
      }

      // Check if the year level is valid
      if (!yearLevels.includes(yearLevel)) {
        console.warn(`Invalid year level "${yearLevelRaw}" (mapped to "${yearLevel}") for Hero ID ${heroId}`);
        errorCount++;
        continue;
      }

      try {
        const payload = {
          _id: student._id.$oid || student._id,
          currentYear: yearLevel,
          academicYear: billingYear.value,
          // Set as new baseline
          firstSystemYearLevel: yearLevel,
          yearLevelStartDate: billingYear.value
        };

        await axios.post('/api/updateStudent', payload);

        // Update local state
        student.currentYear = yearLevel;
        student.academicYear = billingYear.value;
        student.firstSystemYearLevel = yearLevel;
        student.yearLevelStartDate = billingYear.value;
        updatedCount++;
      } catch (err) {
        console.error(`Failed to update student ${heroId}:`, err);
        errorCount++;
      }
    }

    // Reset file input
    event.target.value = '';

    // Show results
    let resultMsg = `Import complete: ${updatedCount} updated`;
    if (notFoundCount > 0) resultMsg += `, ${notFoundCount} not found`;
    if (errorCount > 0) resultMsg += `, ${errorCount} errors`;

    if (notFoundIds.length > 0) {
      console.log('Students not found by Hero ID:', notFoundIds);
    }

    showMessage(resultMsg, errorCount > 0 ? 'error' : 'success');
  };

  reader.readAsText(file);
};

const exportToCsv = () => {
  const csvRows = [];

  // Header row
  csvRows.push(['Full Name', 'ID', 'Current Year Level'].join(','));

  // Data rows - use filteredStudents to respect current filters
  for (const student of filteredStudents.value) {
    const fullName = `${student.firstNameLegal} ${student.lastNameLegal}`;
    const id = student.heroId || '';
    const yearLevel = student.currentYear || '';

    // Escape fields that might contain commas
    csvRows.push([
      `"${fullName}"`,
      id,
      `"${yearLevel}"`
    ].join(','));
  }

  // Create and download the file
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `year-levels-${billingYear.value}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const fetchSettings = async () => {
  try {
    const response = await axios.get('/api/getSettings');
    if (response.data?.billingYear) {
      billingYear.value = response.data.billingYear;
    }
  } catch (error) {
    console.error('Error fetching settings:', error);
  }
};

const fetchStudents = async () => {
  try {
    const response = await axios.get('/api/getStudents');
    students.value = response.data;
  } catch (error) {
    console.error('Error fetching students:', error);
    showMessage('Error fetching students', 'error');
  }
};

onMounted(async () => {
  await fetchSettings();
  await fetchStudents();
});
</script>

<style scoped>
.overflow-x-auto {
  @apply -mx-4 mt-4 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg;
}

/* Add disabled styles */
select:disabled, button:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>